import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";

import { useI18n, type Locale } from "@/lib/i18n";
import { OPERATOR, operatorName } from "@/lib/operator";

type FooterStrings = {
  tagline: string;
  legal: string;
  product: string;
  support: string;
  terms: string;
  privacy: string;
  refund: string;
  cookies: string;
  pricing: string;
  services: string;
  blog: string;
  faq: string;
  contact: string;
  disclaimer: string;
  rights: string;
};

// Short labels only — kept inline (same pattern as $lang.pricing.tsx) so the footer
// works on public pages that render before the app's i18n dictionaries matter.
const UI: Record<Locale, FooterStrings> = {
  en: {
    tagline: "SMS verification, disposable email and verified accounts.",
    legal: "Legal",
    product: "Product",
    support: "Support",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    refund: "Refund Policy",
    cookies: "Cookie Policy",
    pricing: "Pricing",
    services: "All services",
    blog: "Blog",
    faq: "FAQ",
    contact: "Contact",
    disclaimer:
      "Not affiliated with or endorsed by any of the platforms named on this site. Their names identify which service a number works with.",
    rights: "All rights reserved.",
  },
  es: {
    tagline: "Verificación por SMS, correo desechable y cuentas verificadas.",
    legal: "Legal",
    product: "Producto",
    support: "Soporte",
    terms: "Términos del servicio",
    privacy: "Política de privacidad",
    refund: "Política de reembolsos",
    cookies: "Política de cookies",
    pricing: "Precios",
    services: "Todos los servicios",
    blog: "Blog",
    faq: "Preguntas frecuentes",
    contact: "Contacto",
    disclaimer:
      "Sin afiliación ni respaldo de ninguna de las plataformas citadas en este sitio. Sus nombres solo identifican con qué servicio funciona cada número.",
    rights: "Todos los derechos reservados.",
  },
  fr: {
    tagline: "Vérification SMS, e-mail jetable et comptes vérifiés.",
    legal: "Mentions légales",
    product: "Produit",
    support: "Assistance",
    terms: "Conditions d'utilisation",
    privacy: "Politique de confidentialité",
    refund: "Politique de remboursement",
    cookies: "Politique de cookies",
    pricing: "Tarifs",
    services: "Tous les services",
    blog: "Blog",
    faq: "FAQ",
    contact: "Contact",
    disclaimer:
      "Sans affiliation ni approbation des plateformes citées sur ce site. Leurs noms identifient uniquement le service concerné.",
    rights: "Tous droits réservés.",
  },
  de: {
    tagline: "SMS-Verifizierung, Wegwerf-E-Mail und verifizierte Konten.",
    legal: "Rechtliches",
    product: "Produkt",
    support: "Support",
    terms: "Nutzungsbedingungen",
    privacy: "Datenschutzerklärung",
    refund: "Rückerstattungsrichtlinie",
    cookies: "Cookie-Richtlinie",
    pricing: "Preise",
    services: "Alle Dienste",
    blog: "Blog",
    faq: "FAQ",
    contact: "Kontakt",
    disclaimer:
      "Keine Verbindung zu den auf dieser Website genannten Plattformen. Ihre Namen dienen nur der Zuordnung des jeweiligen Dienstes.",
    rights: "Alle Rechte vorbehalten.",
  },
  it: {
    tagline: "Verifica SMS, email usa e getta e account verificati.",
    legal: "Note legali",
    product: "Prodotto",
    support: "Assistenza",
    terms: "Termini di servizio",
    privacy: "Informativa sulla privacy",
    refund: "Politica di rimborso",
    cookies: "Politica sui cookie",
    pricing: "Prezzi",
    services: "Tutti i servizi",
    blog: "Blog",
    faq: "FAQ",
    contact: "Contatti",
    disclaimer:
      "Nessuna affiliazione né approvazione da parte delle piattaforme citate in questo sito. I nomi servono solo a identificare il servizio.",
    rights: "Tutti i diritti riservati.",
  },
  pt: {
    tagline: "Verificação por SMS, email descartável e contas verificadas.",
    legal: "Legal",
    product: "Produto",
    support: "Suporte",
    terms: "Termos de serviço",
    privacy: "Política de privacidade",
    refund: "Política de reembolso",
    cookies: "Política de cookies",
    pricing: "Preços",
    services: "Todos os serviços",
    blog: "Blog",
    faq: "Perguntas frequentes",
    contact: "Contacto",
    disclaimer:
      "Sem afiliação ou aprovação das plataformas mencionadas neste site. Os nomes servem apenas para identificar o serviço.",
    rights: "Todos os direitos reservados.",
  },
  ru: {
    tagline: "SMS-верификация, одноразовая почта и проверенные аккаунты.",
    legal: "Правовая информация",
    product: "Продукт",
    support: "Поддержка",
    terms: "Условия использования",
    privacy: "Политика конфиденциальности",
    refund: "Политика возврата",
    cookies: "Политика cookie",
    pricing: "Цены",
    services: "Все сервисы",
    blog: "Блог",
    faq: "Вопросы и ответы",
    contact: "Контакты",
    disclaimer:
      "Мы не связаны с платформами, упомянутыми на сайте, и не одобрены ими. Их названия лишь указывают, для какого сервиса подходит номер.",
    rights: "Все права защищены.",
  },
  zh: {
    tagline: "短信验证、一次性邮箱与已验证账户。",
    legal: "法律信息",
    product: "产品",
    support: "支持",
    terms: "服务条款",
    privacy: "隐私政策",
    refund: "退款政策",
    cookies: "Cookie 政策",
    pricing: "价格",
    services: "全部服务",
    blog: "博客",
    faq: "常见问题",
    contact: "联系我们",
    disclaimer: "本站与所提及的任何平台均无关联，也未获其认可。平台名称仅用于标明号码适用的服务。",
    rights: "保留所有权利。",
  },
};

export function SiteFooter() {
  const { locale } = useI18n();
  const ui = UI[locale] ?? UI.en;
  const year = new Date().getFullYear();

  const columns: { title: string; links: { label: string; to: string }[] }[] = [
    {
      title: ui.product,
      links: [
        { label: ui.pricing, to: "/$lang/pricing" },
        { label: ui.services, to: "/$lang/sms" },
        { label: ui.blog, to: "/$lang/blog" },
        { label: ui.faq, to: "/$lang/faq" },
      ],
    },
    {
      title: ui.legal,
      links: [
        { label: ui.terms, to: "/$lang/terms" },
        { label: ui.privacy, to: "/$lang/privacy" },
        { label: ui.refund, to: "/$lang/refund" },
        { label: ui.cookies, to: "/$lang/cookies" },
      ],
    },
    {
      title: ui.support,
      links: [{ label: ui.contact, to: "/$lang/contact" }],
    },
  ];

  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-primary shadow-glow">
                <Sparkles className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-base font-bold text-foreground">{OPERATOR.tradingName}</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{ui.tagline}</p>
          </div>

          {columns.map((column) => (
            <nav key={column.title} aria-label={column.title}>
              <h2 className="text-sm font-semibold text-foreground">{column.title}</h2>
              <ul className="mt-3 space-y-2">
                {column.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      params={{ lang: locale }}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-10 space-y-3 border-t border-border pt-6">
          <p className="text-xs text-muted-foreground">{ui.disclaimer}</p>
          <p className="text-xs text-muted-foreground">
            © {year} {operatorName()}. {ui.rights}
            {OPERATOR.supportEmail && (
              <>
                {" · "}
                <a
                  href={`mailto:${OPERATOR.supportEmail}`}
                  className="transition-colors hover:text-foreground"
                >
                  {OPERATOR.supportEmail}
                </a>
              </>
            )}
          </p>
        </div>
      </div>
    </footer>
  );
}
